import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";
import { ArrowDown, ArrowUp, Bold, Heading2, Heading3, Italic, Link2, List, ListOrdered, Quote, Star, Trash2, Upload } from "lucide-react";
import { mediaUrl } from "../lib/api";
import { aapi, apiErr, useA } from "./lib";

const MAX_MB = { image: 10, video: 200, pdf: 30 };
const ACCEPT = { image: "image/jpeg,image/png,image/webp", video: "video/mp4,video/webm,video/quicktime", pdf: "application/pdf" };

export async function uploadFile(kind, file) {
  if (file.size > MAX_MB[kind] * 1024 * 1024) throw new Error(`Max ${MAX_MB[kind]} MB`);
  const fd = new FormData();
  fd.append("file", file);
  const r = await aapi.post(`/admin/upload?kind=${kind}`, fd);
  return r.data.url;
}

export function UploadButton({ kind, onDone, multiple, testid, disabled }) {
  const a = useA();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    for (const f of files) {
      try { onDone(await uploadFile(kind, f)); toast.success(`${f.name} ✓`); } catch (ex) { toast.error(`${f.name}: ${apiErr(ex)}`); }
    }
    setBusy(false);
  };
  return (
    <label className={`chip cursor-pointer ${disabled ? "pointer-events-none opacity-40" : ""}`}>
      <Upload className="h-3.5 w-3.5" />{busy ? a("Uploading…", "جارٍ الرفع…") : a(`Upload ${kind}`, "رفع ملف")}
      <input type="file" className="sr-only" accept={ACCEPT[kind]} multiple={multiple} onChange={pick} data-testid={testid} disabled={busy || disabled} />
    </label>
  );
}

export function ImagesField({ value = [], onChange, testid, disabled }) {
  const a = useA();
  const [url, setUrl] = useState("");
  const move = (i, d) => { const v = [...value]; [v[i], v[i + d]] = [v[i + d], v[i]]; onChange(v); };
  return (
    <div data-testid={testid}>
      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {value.map((src, i) => (
          <div key={src + i} className="group relative overflow-hidden rounded-sm border border-ivory/10">
            <img src={mediaUrl(src)} alt="" className="aspect-[4/3] w-full object-cover" />
            {i === 0 && <span className="absolute start-1 top-1 rounded-sm bg-gold px-1.5 text-[10px] font-bold text-ink">{a("COVER", "الغلاف")}</span>}
            {!disabled && (
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-ink/80 p-1">
                <button type="button" title={a("Set as cover", "تعيين كغلاف")} onClick={() => onChange([src, ...value.filter((_, j) => j !== i)])} data-testid={`${testid}-cover-${i}`}><Star className="h-3.5 w-3.5 text-gold" /></button>
                <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" disabled={i === value.length - 1} onClick={() => move(i, 1)} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`} aria-label="Remove"><Trash2 className="h-3.5 w-3.5 text-[#E4472B]" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          <UploadButton kind="image" multiple onDone={(u) => onChange([...(value || []), u].filter(Boolean))} testid={`${testid}-upload`} />
          <input className="field !w-64 !py-2" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} dir="ltr" />
          <button type="button" className="chip" onClick={() => { if (url.startsWith("http")) { onChange([...value, url]); setUrl(""); } }}>{a("Add URL", "إضافة رابط")}</button>
        </div>
      )}
    </div>
  );
}

export function FileField({ kind, value, onChange, testid, disabled }) {
  const a = useA();
  return (
    <div className="space-y-2">
      {value && kind === "image" && <img src={mediaUrl(value)} alt="" className="h-28 rounded-sm border border-ivory/10 object-cover" />}
      <div className="flex flex-wrap items-center gap-2">
        <input className="field !py-2 sm:!w-96" dir="ltr" value={value || ""} onChange={(e) => onChange(e.target.value || null)} placeholder="https://… or upload" data-testid={testid} disabled={disabled} />
        <UploadButton kind={kind} onDone={onChange} testid={`${testid}-upload`} disabled={disabled} />
        {value && !disabled && <button type="button" className="chip" onClick={() => onChange(null)}>{a("Clear", "مسح")}</button>}
      </div>
    </div>
  );
}

export function MediaListField({ value = [], onChange, testid }) {
  const a = useA();
  const add = (type) => (url) => onChange([...value, { type, url }]);
  return (
    <div data-testid={testid} className="space-y-2">
      {value.map((m, i) => (
        <div key={m.url + i} className="flex items-center gap-2 text-xs">
          <span className="chip !py-1">{m.type}</span>
          <a href={mediaUrl(m.url)} target="_blank" rel="noopener noreferrer" className="truncate text-gold" dir="ltr">{m.url.split("/").pop()}</a>
          <input className="field !w-48 !py-1.5" placeholder={a("Caption (EN)", "التعليق (EN)")} value={m.caption_en || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, caption_en: e.target.value } : x)))} />
          <input className="field !w-48 !py-1.5" placeholder="التعليق (AR)" value={m.caption_ar || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, caption_ar: e.target.value } : x)))} />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remove"><Trash2 className="h-4 w-4 text-[#E4472B]" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <UploadButton kind="video" onDone={add("video")} testid={`${testid}-video`} />
        <UploadButton kind="image" multiple onDone={add("image")} testid={`${testid}-image`} />
        <UploadButton kind="pdf" onDone={add("pdf")} testid={`${testid}-pdf`} />
      </div>
    </div>
  );
}

const pin = L.divIcon({ className: "", html: '<div class="lf-marker lf-marker-sm"></div>', iconSize: [18, 18], iconAnchor: [9, 9] });
function Clicker({ onPick }) { useMapEvents({ click: (e) => onPick(e.latlng) }); return null; }

export function LatLngField({ lat, lng, onChange, disabled, testid }) {
  const center = lat && lng ? [lat, lng] : [23.8, 44.5];
  return (
    <div data-testid={testid}>
      <div className="mb-2 grid grid-cols-2 gap-2">
        <input className="field !py-2" type="number" step="any" value={lat ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value), lng)} placeholder="lat" dir="ltr" disabled={disabled} data-testid={`${testid}-lat`} />
        <input className="field !py-2" type="number" step="any" value={lng ?? ""} onChange={(e) => onChange(lat, e.target.value === "" ? null : Number(e.target.value))} placeholder="lng" dir="ltr" disabled={disabled} data-testid={`${testid}-lng`} />
      </div>
      <div className="h-64 overflow-hidden rounded-sm border border-gold/20">
        <MapContainer center={center} zoom={lat ? 11 : 5} className="h-full w-full">
          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" className="lf-dark-tiles" />
          {!disabled && <Clicker onPick={(p) => onChange(+p.lat.toFixed(6), +p.lng.toFixed(6))} />}
          {lat && lng && <Marker position={[lat, lng]} icon={pin} />}
        </MapContainer>
      </div>
    </div>
  );
}

const CMDS = [["bold", Bold], ["italic", Italic], ["h2", Heading2], ["h3", Heading3], ["insertUnorderedList", List], ["insertOrderedList", ListOrdered], ["blockquote", Quote], ["link", Link2]];

export function RichText({ value, onChange, testid, dir }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || ""; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = (c) => {
    ref.current.focus();
    if (c === "link") { const u = window.prompt("https://"); if (u && u.startsWith("https://")) document.execCommand("createLink", false, u); }
    else if (["h2", "h3", "blockquote"].includes(c)) document.execCommand("formatBlock", false, c);
    else document.execCommand(c);
    onChange(ref.current.innerHTML);
  };
  return (
    <div className="rounded-sm border border-ivory/15">
      <div className="flex flex-wrap gap-1 border-b border-ivory/10 p-1.5">
        {CMDS.map(([c, Icon]) => (<button key={c} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => run(c)} className="rounded p-1.5 text-ivory/70 hover:bg-ivory/10 hover:text-gold" aria-label={c}><Icon className="h-4 w-4" /></button>))}
      </div>
      <div ref={ref} dir={dir} contentEditable suppressContentEditableWarning data-testid={testid} onInput={(e) => onChange(e.currentTarget.innerHTML)} className="prose-lux min-h-[220px] space-y-3 p-4 text-sm text-ivory/85 focus:outline-none" />
    </div>
  );
}
